import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Table, Modal } from 'react-bootstrap';
import api from '../services/api';
import ExecutionTable from '../components/common/ExecutionTable';
import { useExecution } from '../contexts/ExecutionContext';

const ProcessGuessingPage = () => {
  // Get execution context
  const {
    isModuleRunning,
    getModuleProgress,
    getRunningExecutionId,
    startExecution,
    isAnyModuleRunning,
    getRunningModuleName,
    executionCompletionTrigger,
    executionStartTrigger
  } = useExecution();
  
  // States for execution data
  const [executions, setExecutions] = useState([]);
  
  // Pagination state for executions
  const [executionPage, setExecutionPage] = useState(1);
  const [executionTotalPages, setExecutionTotalPages] = useState(1);
  const [executionSortOrder, setExecutionSortOrder] = useState("desc");
  
  // eslint-disable-next-line no-unused-vars
  const [lastExecutionId, setLastExecutionId] = useState(null);

  // Module-specific state derived from centralized execution context
  const moduleName = "process_guesser";
  const isRunning = isModuleRunning(moduleName);
  const runProgress = getModuleProgress(moduleName);
  const currentExecutionId = getRunningExecutionId(moduleName);
  
  // States for results table
  const [eventMasterResults, setEventMasterResults] = useState([]);
  const [originalExecutionId, setOriginalExecutionId] = useState(null);
  
  // Form states
  const [userInstruction, setUserInstruction] = useState('');
  const [runMode, setRunMode] = useState('full');
  const [useReferenceEventMaster, setUseReferenceEventMaster] = useState(false);

  // State for error messages
  const [error, setError] = useState(null);

  // New states for Event Master functionality
  const [dataSource, setDataSource] = useState('suggestion'); // 'suggestion' or 'eventmaster'
  const [isLoadingEventMaster, setIsLoadingEventMaster] = useState(false);
  const [isSavingEventMaster, setIsSavingEventMaster] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // State for basic statistics
  const [basicStats, setBasicStats] = useState(null);

  const fetchExecutions = useCallback(async () => {
    try {
      const response = await api.getExecutions(
        "process_guesser",
        "timestamp_start",
        executionSortOrder,
        executionPage,
        4
      );
      const executionData = response.data?.executions || [];
      setExecutions(executionData);
      setExecutionTotalPages(response.data?.nr_of_pages || 1);
      if (executionData.length > 0) {
        setLastExecutionId(executionData[0].execution_id);
      }
    } catch (err) {
      console.error('Error fetching executions:', err);
      setError('Failed to load execution history.');
    }
  }, [executionPage, executionSortOrder]);

  const fetchBasicStatistics = useCallback(async () => {
    try {
      const response = await api.getBasicStatistics();
      setBasicStats(response.data);
    } catch (err) {
      console.error('Error fetching basic statistics:', err);
      // Don't set error state for statistics as it's not critical
    }
  }, []);

  const runProcessGuesser = async () => {
    try {
      // Check if any module is already running
      if (isAnyModuleRunning()) {
        const runningModule = getRunningModuleName();
        setError(`Cannot start process guesser. ${runningModule} is currently running.`);
        return;
      }

      setError(null);

      const params = {
        run_in_background: true,
      };

      if (userInstruction) {
        params.user_instruction = userInstruction;
      }

      // Only add remerge_id for remerge mode, and only if originalExecutionId exists
      if (runMode === 'remerge' && originalExecutionId) {
        params.remerge_id = originalExecutionId;
      }
      // For Full Run mode, never add remerge parameter

      let reference_event_master = null;
      if (useReferenceEventMaster) {
        reference_event_master = eventMasterResults;
      }

      const response = await api.runProcessGuesser(reference_event_master, params);
      
      if (response.data && response.data.execution_id) {
        if (response.data.error_flag) {
          throw new Error(`Failed to start process guesser: ${response.data.error_message || 'Unknown error'}`);
        }
        // Start tracking this execution in the centralized state
        startExecution(moduleName, response.data.execution_id);
      } else {
        throw new Error('No execution ID returned');
      }
    } catch (err) {
      console.error('Error running process guesser:', err);
      setError('Failed to start process guesser.');
    }
  };

  const loadEventMaster = async () => {
    try {
      setIsLoadingEventMaster(true);
      setError(null);
      setSuccessMessage(null);

      const response = await api.getEventMaster();
      setEventMasterResults(response.data || []);
      setOriginalExecutionId(null); // Event Master directly loaded has no original execution
      setDataSource('eventmaster');
      setSuccessMessage('Event Master loaded successfully.');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error loading event master:', err);
      setError('Failed to load Event Master.');
    } finally {
      setIsLoadingEventMaster(false);
    }
  };

  const writeEventMaster = async () => {
    try {
      setIsSavingEventMaster(true);
      setError(null);
      setSuccessMessage(null);

      await api.setEventMaster(eventMasterResults);
      setSuccessMessage('Event Master saved successfully.');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving event master:', err);
      setError('Failed to save Event Master.');
    } finally {
      setIsSavingEventMaster(false);
      setShowConfirmDialog(false);
    }
  };

  const handleWriteEventMaster = () => {
    if (eventMasterResults.length === 0) {
      setError('No data to save. Please load Event Master or run Process Guesser first.');
      return;
    }
    setShowConfirmDialog(true);
  };

  const downloadEventMaster = () => {
    if (eventMasterResults.length === 0) {
      setError('No data to download. Please load Event Master or run Process Guesser first.');
      return;
    }

    try {
      const dataStr = JSON.stringify(eventMasterResults, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `event_master_${dataSource}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      setSuccessMessage('Event Master downloaded successfully.');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error downloading event master:', err);
      setError('Failed to download Event Master.');
    }
  };

  useEffect(() => {
    fetchExecutions();
    fetchBasicStatistics();
  }, [fetchExecutions, fetchBasicStatistics]);

  // Refresh data and fetch results when any execution completes
  useEffect(() => {
    if (executionCompletionTrigger > 0) {
      // An execution has completed, refresh data and fetch results
      fetchExecutions();
      
      // Get the most recent execution to fetch results
      const fetchResults = async () => {
        try {
          const recentResponse = await api.getMostRecentExecution("process_guesser");
          if (recentResponse.data && recentResponse.data.executions && recentResponse.data.executions.length > 0) {
            const execution = recentResponse.data.executions[0];
            const resultsResponse = await api.getEventMasterSuggestion(execution.execution_id);
            if (resultsResponse.data) {
              setEventMasterResults(resultsResponse.data.event_master_suggestion_entries || []);
              setOriginalExecutionId(resultsResponse.data.original_execution_id || null);
              setDataSource('suggestion');
            }
          }
        } catch (err) {
          console.error('Error fetching results:', err);
        }
      };
      
      fetchResults();
    }
  }, [executionCompletionTrigger, fetchExecutions]);

  // Refresh execution history when any execution starts
  useEffect(() => {
    if (executionStartTrigger > 0) {
      // An execution has started, refresh execution history to show the new running entry
      fetchExecutions();
    }
  }, [executionStartTrigger, fetchExecutions]);

  return (
    <Container className="py-4">
      <h1 className="mb-4">Process Guessing</h1>
      
      {basicStats && (
        <p className="text-muted mb-4">
          Based on <strong>{basicStats.nr_messages_ready_for_process_guessing}</strong> available messages
        </p>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>Run Process Guesser</Card.Header>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Run Mode</Form.Label>
                      <Form.Select 
                        value={runMode} 
                        onChange={(e) => setRunMode(e.target.value)}
                        disabled={isRunning}
                      >
                        <option value="full">Full Run</option>
                        <option value="remerge" disabled={!originalExecutionId}>Remerge Current Table</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>User Instruction</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        placeholder="Enter optional instruction"
                        value={userInstruction}
                        onChange={(e) => setUserInstruction(e.target.value)}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onKeyDown={(e) => {
                          if (e.ctrlKey && e.key === 'Enter') {
                            e.preventDefault();
                            if (!isRunning) {
                              runProcessGuesser();
                            }
                          }
                        }}
                        disabled={isRunning}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="checkbox"
                    label="Use current results as reference"
                    checked={useReferenceEventMaster}
                    onChange={(e) => setUseReferenceEventMaster(e.target.checked)}
                    disabled={isRunning || eventMasterResults.length === 0}
                  />
                </Form.Group>
                <Button 
                  variant="primary" 
                  onClick={runProcessGuesser}
                  disabled={isRunning}
                  className="w-100"
                >
                  {isRunning ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Running... ({runProgress}%)
                    </>
                  ) : (
                    'Run Process Guesser'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>
              {dataSource === 'eventmaster' ? 'Active Event Master' : 'Event Master Suggestion'}
            </h3>
            <div>
              <Button
                variant="outline-primary"
                onClick={loadEventMaster}
                disabled={isLoadingEventMaster || isRunning}
                className="me-2"
              >
                {isLoadingEventMaster ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  'Load Event Master'
                )}
              </Button>
              <Button
                variant="outline-info"
                onClick={downloadEventMaster}
                disabled={eventMasterResults.length === 0}
                className="me-2"
              >
                Download Event Master
              </Button>
              <Button
                variant="outline-success"
                onClick={handleWriteEventMaster}
                disabled={isSavingEventMaster || eventMasterResults.length === 0}
              >
                {isSavingEventMaster ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  'Write Event Master'
                )}
              </Button>
            </div>
          </div>
          <Card>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Identifier</th>
                    <th>Message Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {eventMasterResults.length > 0 ? (
                    eventMasterResults.map((result, index) => (
                      <tr key={index}>
                        <td>{result.title}</td>
                        <td>{result.description}</td>
                        <td>{result.identifier}</td>
                        <td>{result.message_direction}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No results to display.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <h3>Execution History</h3>
          <ExecutionTable
            executions={executions}
            title=""
            currentPage={executionPage}
            totalPages={executionTotalPages}
            pageSize={4}
            sortOrder={executionSortOrder}
            showResults={false}
            onPageChange={(page) => setExecutionPage(page)}
            onSortChange={(sortOrder) => setExecutionSortOrder(sortOrder)}
            onExecutionClick={async (executionId) => {
              try {
                const response = await api.getEventMasterSuggestion(executionId);
                if (response.data) {
                  setEventMasterResults(response.data.event_master_suggestion_entries || []);
                  setOriginalExecutionId(response.data.original_execution_id || null);
                  setDataSource('suggestion');
                }
              } catch (err) {
                console.error('Error fetching event master suggestion:', err);
                setError('Failed to load event master suggestion.');
              }
            }}
          />
        </Col>
      </Row>

      {/* Confirmation Dialog */}
      <Modal show={showConfirmDialog} onHide={() => setShowConfirmDialog(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Save</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to overwrite the current Event Master configuration?</p>
          <p>This action will replace the existing Event Master with the data currently displayed in the table.</p>
          <p><strong>Number of entries to save:</strong> {eventMasterResults.length}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={writeEventMaster} disabled={isSavingEventMaster}>
            {isSavingEventMaster ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              'Save Event Master'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProcessGuessingPage;